// /webinar-thank-you — confirmation page ActiveCampaign redirects to after a
// successful webinar-form submission.
//
// Purpose:
//   1. Confirm the registration to the user.
//   2. Recap the event details (date, time, format).
//   3. Make adding the event to a personal calendar a one-click action across
//      Google, Outlook (both flavors), Apple, Yahoo + ICS download.
//   4. Give a tight "what happens next" recap.
//   5. Fire the conversion event so Meta/GA4/Google Ads all attribute it.
//
// Webinar date + ICS / Google / Outlook URL builders live in
// src/lib/webinar.js so this page and /webinar always reference the same
// source of truth — change the date there, both pages update.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { CookieBanner, AAConsent } from './CookieBanner.jsx';
import { CalendarGrid } from './CalendarSection.jsx';
import { trackWebinarRegistration } from '../lib/tracking.js';
import { readCapturedEmailHash, clearCapturedEmailHash, computeEventId, hasAlreadyFired, markFired } from '../lib/webinar-lead-id.js';
import { updateGoogleConsent } from '../lib/google-ads.js';
import { enableMetaPixel } from '../lib/meta-pixel.js';
import { enableAnalytics } from '../lib/analytics.js';
import {
  WEBINAR_DATE,
  WEBINAR_FORMAT,
  DATE_FMT,
  TIME_FMT,
  isWebinarPast,
  WEBINAR_TBD_LABEL,
  WHATSAPP_GROUP_URL,
} from '../lib/webinar.js';

// ─── PRE-FLIGHT CONSENT + TRACKING (runs at module load, before React mounts) ─
//
// Anyone who hits /webinar-thank-you just submitted the ActiveCampaign form,
// so we treat the visit as click-wrap consent for Functional + Statistics +
// Marketing across every ad/analytics channel. Doing this at MODULE LOAD
// (rather than in a useEffect) buys us three things:
//
//   1. NO COOKIE-BANNER FLASH. The CookieBanner's mount-time effect runs
//      readConsent() once and sets a 400 ms timer to show the banner if
//      nothing is stored. By writing consent at module-load — before
//      ReactDOM.render even runs — readConsent() finds the granted record
//      on its first pass and the banner stays hidden.
//
//   2. SDK HEAD-START. enableMetaPixel() / enableAnalytics() are invoked
//      synchronously here, so fbq's queue is set up + the script tag is in
//      the DOM by the time React commits the page. The subsequent Lead
//      event then lands in an already-initialised pixel rather than racing
//      the script's network round-trip.
//
//   3. GOOGLE ADS CONSENT MODE FLIP. updateGoogleConsent() pushes
//      consent=granted onto the dataLayer BEFORE trackAdsConversion fires,
//      so Google Ads records the conversion as granted (not "denied with
//      modeling fallback") — required for accurate conversion attribution.
//
// This module body is evaluated exactly once per page navigation, which is
// the correct cardinality for a thank-you-page conversion.
//
// PATH GUARD: main.jsx imports every route module at app boot, so this
// module body runs on EVERY page, not just /webinar-thank-you. Without the
// path check below, a first-time visitor to any route (/, /webinar, /live,
// …) would get all consent categories auto-granted before the CookieBanner
// even mounts — meaning the banner would never appear at all. Only flip
// consent when the user is actually landing on the thank-you page.
if (
  typeof window !== 'undefined' &&
  /^\/webinar-thank-you\/?$/i.test(window.location.pathname)
) {
  try {
    const current = AAConsent.current();
    if (!current.functional || !current.statistics || !current.marketing) {
      AAConsent.set({ functional: true, statistics: true, marketing: true });
    }
    // Belt-and-suspenders: AAConsent.set already notifies the bootServices
    // listener registered by CookieBanner, but we call these directly too in
    // case that listener was somehow torn down (e.g. HMR in dev).
    updateGoogleConsent({ statistics: true, marketing: true });
    enableMetaPixel();
    enableAnalytics();
  } catch (e) {
    // Never let a tracking-priming error block the page render.
    if (typeof console !== 'undefined') console.warn('[thank-you] consent prime failed:', e);
  }
}

// ─── HEAD ──────────────────────────────────────────────────────────────────
function useThankYouHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Danke! Du bist für das AI App Academy Webinar angemeldet.';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'Deine Anmeldung zum kostenlosen AI App Academy Webinar ist bestätigt. Trage dir den Termin jetzt in deinen Kalender ein.'
      );
    }
    // Prevent search engines from indexing the thank-you page.
    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
      if (hadRobots && prevRobots != null) robots.setAttribute('content', prevRobots);
      else if (!hadRobots && robots) robots.remove();
    };
  }, []);
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function ThankYouNav() {
  return (
    <nav className="nav nav-webinar">
      <div className="nav-inner">
        <a href="/" className="logo">
          <div className="logo-mark"><LogoMark size={32} /></div>
          <span className="logo-text">AI App Academy</span>
        </a>
      </div>
    </nav>
  );
}

function ThankYouHero() {
  return (
    <section className="hero webinar-hero thankyou-hero" id="top">
      <div className="hero-bg-grid" aria-hidden="true"></div>
      <div className="container">
        <div className="thankyou-success-badge">
          <Icon.Check size={32} />
        </div>

        <div className="webinar-eyebrow">
          <span className="webinar-live-dot"></span>
          <span>Anmeldung bestätigt</span>
        </div>

        <h1 className="hero-headline webinar-headline thankyou-headline">
          Du bist <em>dabei</em>.
        </h1>

        <p className="thankyou-sub">
          Deine Anmeldung zum kostenlosen Live-Webinar ist bestätigt. Jetzt fehlen nur noch
          zwei kurze Schritte, damit du deinen Platz voll nutzt.
        </p>

        <div className="event-meta-pill">
          {isWebinarPast() ? (
            <span className="event-meta-item">
              <Icon.Calendar size={16} />
              <span>{WEBINAR_TBD_LABEL}</span>
            </span>
          ) : (
            <>
              <span className="event-meta-item">
                <Icon.Calendar size={16} />
                <span>{DATE_FMT.format(WEBINAR_DATE)}</span>
              </span>
              <span className="event-meta-divider"></span>
              <span className="event-meta-item">
                <Icon.Clock size={16} />
                <span>{TIME_FMT.format(WEBINAR_DATE)} Uhr</span>
              </span>
              <span className="event-meta-divider"></span>
              <span className="event-meta-item">
                <span>{WEBINAR_FORMAT}</span>
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// Guided post-signup flow: the two actions the attendee must still take, shown
// as numbered steps so it's unmistakable what to do next and why it pays off.
// Calendar first (most important: locks the date in so it isn't forgotten),
// WhatsApp second (link + reminders => fewer no-shows).
function NextActionsSection() {
  // If the real WhatsApp invite isn't configured yet, that step is dropped and
  // the calendar is the only step (still numbered 1).
  const hasWhatsApp = WHATSAPP_GROUP_URL && !WHATSAPP_GROUP_URL.includes('REPLACE_WITH_REAL_INVITE');
  const onJoin = () => {
    try { window.dataLayer && window.dataLayer.push({ event: 'whatsapp_group_join' }); } catch (e) {}
  };
  const perks = [
    { icon: <Icon.Play size={18} />, text: 'Den Live-Link bekommst du dort zuerst, kein Suchen im Postfach, kein Verpassen.' },
    { icon: <Icon.Clock size={18} />, text: 'Kurze Erinnerung vor dem Start, damit dein Platz nicht verfällt.' },
    { icon: <Icon.FileText size={18} />, text: 'Vorbereitungs-Tipps und Material zum Thema schon vor dem Webinar.' },
    { icon: <Icon.Mail size={18} />, text: 'Stell deine Frage vorab, die besten beantworte ich live im Q&A.' },
  ];
  return (
    <section className="section thankyou-actions-section">
      <div className="container">
        <div className="section-head thankyou-actions-head">
          <span className="eyebrow">Fast geschafft</span>
          <h2 className="section-title">
            {hasWhatsApp ? 'Nur noch 2 Schritte' : 'Nur noch 1 Schritt'}, dann bist du{' '}
            <span className="serif-italic">startklar</span>.
          </h2>
          <p className="lead">
            Deine Anmeldung ist gesichert. Damit du deinen Platz wirklich nutzt und nichts
            verpasst, erledige das jetzt gleich. Es dauert keine Minute.
          </p>
        </div>

        <ol className="thankyou-steps">
          <li className="thankyou-step">
            <div className="thankyou-step-num" aria-hidden="true">1</div>
            <div className="thankyou-step-body">
              <div className="thankyou-step-tag"><Icon.Calendar size={16} /> Kalender</div>
              <h3 className="thankyou-step-title">Trag den Termin in deinen Kalender ein</h3>
              <p className="thankyou-step-lead">
                Ein Klick und der Termin steht fix drin, inklusive Erinnerung. So verpasst du das
                Webinar garantiert nicht. Titel, Datum, Uhrzeit und Dauer sind schon vorausgefüllt.
              </p>
              <CalendarGrid source="thankyou" />
              <p className="thankyou-step-note">
                <Icon.Sparkles size={14} /> <span>Tipp: Bring eine konkrete App-Idee mit. In der
                Live-Q&A bekommst du damit die wertvollste Antwort.</span>
              </p>
            </div>
          </li>

          {hasWhatsApp && (
            <li className="thankyou-step thankyou-step-whatsapp">
              <div className="thankyou-step-num" aria-hidden="true">2</div>
              <div className="thankyou-step-body">
                <div className="thankyou-step-tag"><WhatsAppIcon size={16} /> WhatsApp-Gruppe</div>
                <h3 className="thankyou-step-title">Tritt der Webinar-Gruppe bei</h3>
                <p className="thankyou-step-lead">
                  Hier läuft alles rund um das Webinar zusammen. Der schnellste Weg, wirklich dabei
                  zu sein und das Maximum mitzunehmen:
                </p>
                <ul className="thankyou-whatsapp-perks">
                  {perks.map((p, i) => (
                    <li key={i}><span className="thankyou-whatsapp-perk-icon">{p.icon}</span> <span>{p.text}</span></li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_GROUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onJoin}
                  className="btn btn-primary btn-xl thankyou-whatsapp-btn"
                >
                  <WhatsAppIcon size={20} />
                  <span>Jetzt der WhatsApp-Gruppe beitreten</span>
                  <span className="btn-arrow"><Icon.ArrowRight size={18} /></span>
                </a>
                <p className="thankyou-whatsapp-reassure">
                  Nur für angemeldete Teilnehmer · Kein Spam · Jederzeit wieder verlassbar.
                </p>
              </div>
            </li>
          )}
        </ol>
      </div>
    </section>
  );
}

// Inline WhatsApp glyph so no external asset/CSP entry is needed.
function WhatsAppIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.04 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.38 9.38 0 0 1-1.44-5A9.42 9.42 0 0 1 18.72 5.3a9.36 9.36 0 0 1 2.76 6.66c0 5.19-4.23 9.42-9.44 9.42zm8.02-17.44A11.36 11.36 0 0 0 12.04.5C5.8.5.72 5.58.72 11.82c0 2.08.55 4.11 1.58 5.9L.62 23.5l5.92-1.55a11.3 11.3 0 0 0 5.5 1.4h.01c6.24 0 11.32-5.08 11.32-11.32 0-3.03-1.18-5.87-3.31-8.01z"/>
    </svg>
  );
}

function ThankYouOutro() {
  return (
    <section className="cta-final thankyou-outro" id="outro">
      <div className="container">
        <div className="cta-final-content">
          <div className="webinar-final-logo">
            <LogoMark size={56} />
          </div>
          <h2>
            Bis bald, <em className="serif-italic">live</em> im Webinar.
          </h2>
          <p>
            {isWebinarPast()
              ? WEBINAR_TBD_LABEL + '. Wir melden uns rechtzeitig.'
              : `${DATE_FMT.format(WEBINAR_DATE)} um ${TIME_FMT.format(WEBINAR_DATE)} Uhr. Wir sehen uns dort.`}
          </p>
          <p className="thankyou-signature">Dominik &amp; das App-Academy-Team</p>
        </div>
      </div>
    </section>
  );
}

function ThankYouFooter() {
  return (
    <footer className="webinar-footer">
      <div className="container">
        <div className="webinar-footer-row">
          <div className="webinar-footer-brand">
            <div className="logo-mark"><LogoMark size={24} /></div>
            <span>© 2026 Incibit GmbH · App Academy</span>
          </div>
          <div className="webinar-footer-links">
            <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
            <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
            <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
            <a
              href="#cookie-settings"
              onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
            >
              Cookie-Einstellungen
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function WebinarThankYouPage() {
  useThankYouHead();

  // Fire the conversion event on mount. By the time this runs, the module-load
  // pre-flight above has already (a) granted consent across all channels, (b)
  // initialised the Meta Pixel fbq queue, (c) enabled GA4 analytics, and (d)
  // pushed gtag('consent','update','granted') onto the dataLayer. So Meta
  // Lead + CompleteRegistration, GA4 generate_lead + webinar_registration,
  // and the Google Ads conversion all land with consent live and the SDKs
  // ready — no race, no first-event-eaten-by-init.
  React.useEffect(() => {
    // Read the email-hash captured on the /webinar page just before AC's form
    // POST (see attachWebinarLeadIdCapture). Derive a matching event_id so the
    // browser Pixel event + server-side CAPI event (from functions/index.js)
    // land at Meta with the same id — Meta then deduplicates explicitly.
    // Missing hash (direct navigation, private-mode edge case) is fine —
    // event_id is optional; Meta falls back to fuzzy dedup on user_data.
    const emailHash = readCapturedEmailHash();
    const eventId = emailHash ? computeEventId(emailHash) : undefined;
    // Reload guard: if this exact event_id already fired from this browser
    // (e.g. the user reloaded the thank-you page), don't fire a second Lead.
    // Meta would dedupe same-id events anyway, but skipping is cleaner and
    // avoids an undeduped fire if the reload somehow lost the id.
    if (eventId && hasAlreadyFired(eventId)) return;
    trackWebinarRegistration({ method: 'activecampaign_form', eventId });
    if (eventId) markFired(eventId);
    clearCapturedEmailHash();
  }, []);

  // Reveal-on-scroll — same pattern as the main app.
  React.useEffect(() => {
    const sections = document.querySelectorAll('section');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    sections.forEach((s) => {
      s.classList.add('reveal-on-scroll');
      const rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) s.classList.add('in');
      io.observe(s);
    });
    return () => io.disconnect();
  }, []);

  return (
    <>
      <ThankYouNav />
      <ThankYouHero />
      <NextActionsSection />
      <ThankYouOutro />
      <ThankYouFooter />
      <CookieBanner />
    </>
  );
}
